import Tool from '../Tool';

/** Measurement line tool base class (line + time and percent label) */
export default class MeasurementLineTool extends Tool {
	_getDefaultParams() {
		return {
			id: 'measurementline',
			name: 'Measurement Line',
			style: this.core.getStyle('tool.measurementLine')
		};
	}

	onClickCanvas(mouse) {
		const core = this.panel.core;
		const { state } = core;
		const isUpper = this.panel.isUpper;
		const numberOfAnchors = this.params.numberOfAnchors || 2;

		// called from core.onClick() on both first and second clicks.
		if (!this.state.inProgressHandle) {
			let mousePanel = [mouse[0], mouse[1] - this.panel.size.top];

			// use snapHandle coords if available
			if (isUpper && state.snapHandle && state.snapHandle.coords) {
				mousePanel = [
					state.snapHandle.coords[0],
					state.snapHandle.coords[1] - this.panel.size.top
				];
			}

			// first click - create and render first handle
			this.handle[0] = this.createHandle(mousePanel);
			this.handle[0].state.visible = true;

			// create second handle (for its coords) but don't render
			this.handle[1] = this.createHandle(mousePanel);

			// Exit out of the handle placement if only one handle is necessary
			if (numberOfAnchors === 1) {
				this.state.inProgressHandle = false;
				this.state.dragHandle = null;
				return;
			}
			this.state.dragHandle = this.handle[1];

			// tool is now active
			this.state.inProgressHandle = true;
		} else {
			// use snapHandle coords if available
			if (
				isUpper &&
				state.snapHandle &&
				state.snapHandle.coords &&
				!this.params.lockVertical
			) {
				const snapDateIdx = Math.round(
					this.panel.xAxis.primaryScale.invert(state.snapHandle.coords[0])
				);

				if (core.exchangeDates[snapDateIdx]) {
					let val = this.panel.yAxis.primaryScale.invert(
						state.snapHandle.coords[1]
					);

					// always store handle values in actuals
					if (this.panel.isNormalized) {
						val = this.panel.normalizedToActual(val);
					}

					this.state.dragHandle.params.value = val;
					this.state.dragHandle.params.date = new Date(
						core.exchangeDates[snapDateIdx]
					);
				}
			}

			// second click
			this.state.inProgressHandle = false; // no longer actively drawing a change in the tool
			this.state.dragHandle = null;
			this.handle[1].state.visible = true;

			this.onComplete();
		}
	}

	onMousemove(mouse) {
		const core = this.panel.core;
		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		core.onToolMousemove(this);

		if (this.state.inProgressHandle) {
			let idx = Math.round(
				this.panel.xAxis.primaryScale.invert(mousePanel[0])
			);

			idx = Math.min(core.exchangeDates.length - 1, idx);
			idx = Math.max(0, idx);

			this.state.dragHandle.params.date = core.exchangeDates[idx];

			if (!this.params.lockVertical) {
				let val = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

				// always store handle values in actuals
				if (this.panel.isNormalized) {
					val = this.panel.normalizedToActual(val);
				}

				this.state.dragHandle.params.value = val;
			}
		} else {
			this.dragTool(mousePanel);
		}
	}

	/**
	 * return true if current mouse coordinates are within hitbox of this tool
	 */
	isWithin(mousePanel) {
		return this._isWithinLineTool(mousePanel);
	}

	_isWithinLineTool(mousePanel) {
		const handles = this.handle;

		if (handles.length === 2) {
			// use either regular handle coords or special endpoint coords (if present)
			const coords = [
				this.state.endpointLeft || handles[0].getCoords(),
				this.state.endpointRight || handles[1].getCoords()
			];

			// couldn't find one or more handles
			if (!coords[0] || !coords[1]) {
				return false;
			}

			let tolerance = 5 + this.params.style.width / 2;
			const isVertical = coords[0][0] === coords[1][0];
			const left = Math.min(coords[0][0], coords[1][0]);
			const right = Math.max(coords[0][0], coords[1][0]);
			const top = Math.min(coords[0][1], coords[1][1]);
			const bottom = Math.max(coords[0][1], coords[1][1]);

			// use a larger hitbox for touch events
			if (this.panel.core.state.isTouchDevice) {
				tolerance += 10;
			}

			// handle vertical lines as a simple bounding box intersection check
			if (isVertical) {
				const r1 = {
					x: left - tolerance,
					y: top,
					width: right - left + 2 * tolerance,
					height: bottom - top
				};
				const r2 = {
					x: mousePanel[0],
					y: mousePanel[1],
					width: 1,
					height: 1
				};

				return this.intersect(r1, r2);
			}

			// handle diagonal or horizontal lines with a distance calculation
			const linepoint = this._getClosestPoint(
				coords[0][0],
				coords[0][1],
				coords[1][0],
				coords[1][1],
				mousePanel[0],
				mousePanel[1]
			);
			const dx = mousePanel[0] - linepoint[0];
			const dy = mousePanel[1] - linepoint[1];
			const distance = Math.abs(Math.sqrt(dx * dx + dy * dy));

			if (
				distance > tolerance ||
				mousePanel[0] < left ||
				mousePanel[0] > right
			) {
				return false;
			} else if (distance < tolerance) {
				return true;
			}
		}
	}

	_getPctChange(rightHandle, leftHandle) {

		const pctChange =
			(
				(
					Number(rightHandle.params.value) -
					Number(leftHandle.params.value)
				) /
				Math.abs(Number(leftHandle.params.value))
			) *
			100;

		return pctChange;
	}

	getLabel(rightHandle, leftHandle) {
		const pctChange = this._getPctChange(rightHandle, leftHandle);
		let prefix = '+';
		let suffix = 'days';
		const timeframe = Math.round(
			(rightHandle.params.date - leftHandle.params.date) /
					(1000 * 60 * 60 * 24)
		);

		// "+/-" prefix
		if (pctChange < 0) prefix = '';

		// don't show timeframe on intraday
		if (this.panel.core.state.isIntraday || timeframe < 1) {
			return `${prefix + pctChange.toFixed(2)}%`;
		}

		// day/days
		if (timeframe === 1) suffix = 'day';

		return `${prefix}${pctChange.toFixed(2)}% (${timeframe} ${suffix})`;
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		// Couldn't find one or more handles
		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;

		let rightHandle = this.handle[0];
		let leftHandle = this.handle[1];
		let rightCoords = coords[0];
		let leftCoords = coords[1];

		if (coords[1][0] >= coords[0][0]) {
			// 2nd handle is further right
			rightHandle = this.handle[1];
			leftHandle = this.handle[0];
			rightCoords = coords[1];
			leftCoords = coords[0];
		}

		// Draw background rectangle (optional)
		if (this.params.style.background.color) {
			ctx.save();
			ctx.fillStyle = this.params.style.background.color;
			ctx.fillRect(
				leftCoords[0],
				0,
				rightCoords[0] - leftCoords[0],
				this.panel.size.height
			);
			ctx.restore();
		}

		// Draw line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.color;
		ctx.lineWidth = this.params.style.width;
		ctx.moveTo(coords[0][0], coords[0][1]);
		ctx.lineTo(coords[1][0], coords[1][1]);
		ctx.stroke();

		// Check if a custom label callback is registered for MeasurementLine
		const isLabelCallbackRegistered = this.panel.core.callbackManager.isRegistered('TOOL_MEASUREMENTLINE_LABEL');

		// Prepare arguments for the callback
		const callbackArgs = {
			start: {
				date: new Date(leftHandle.params.date),
				value: Number(leftHandle.params.value)
			},
			end: {
				date: new Date(rightHandle.params.date),
				value: Number(rightHandle.params.value)
			},
			pctChange: this._getPctChange(rightHandle, leftHandle)
		};

		// Determine the label text
		const labelText = isLabelCallbackRegistered
			? this.panel.core.callbackManager.emit('TOOL_MEASUREMENTLINE_LABEL', callbackArgs)
			: this.getLabel(rightHandle, leftHandle);

		// Set text styles
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		const fontSize = this.params.style.font.size;
		const fontSizePx = fontSize * 1.333;
		const labelMargin = this.params.style.label.margin || 0;
		const labelPadding = this.params.style.label.background.padding;
		ctx.font = this.panel.getFontStyle(
			this.params.style.font.weight,
			fontSize,
			this.params.style.font.family
		);
		ctx.fillStyle = this.params.style.label.color;

		// Determine the text and its measurements
		const textMetrics = ctx.measureText(labelText);
		const textWidth = textMetrics.width;
		const textHeight = fontSizePx;

		// Calculate position and size for text and its background
		let backgroundX = rightCoords[0] + labelMargin;
		let backgroundY = rightCoords[1] - textHeight / 2 - labelPadding.top;
		const backgroundWidth = textWidth + labelPadding.left + labelPadding.right;
		const backgroundHeight = textHeight + labelPadding.top + labelPadding.bottom;
		let textX = backgroundX + backgroundWidth / 2; // Initially center of the background rectangle
		let textY;

		// Horizontal Collision detection with the right edge of the panel
		const panelRightEdge = this.panel.size.width;
		const labelRightEdge = backgroundX + backgroundWidth;
		if (labelRightEdge + 5 > panelRightEdge) {
			// Adjust backgroundX and textX to be right-justified to the right handle
			backgroundX = rightCoords[0] - backgroundWidth - labelMargin;
			textX = backgroundX + backgroundWidth / 2;
		}

		// Vertical Collision detection with the top and bottom edges of the panel
		const panelTopEdge = 0;
		const panelBottomEdge = this.panel.size.height;
		if (backgroundY < panelTopEdge + 5) {
			// If the top edge is too close to the panel's top, adjust it downwards
			backgroundY = panelTopEdge + 5;
		} else if (backgroundY + backgroundHeight > panelBottomEdge - 5) {
			// If the bottom edge is too close to the panel's bottom, adjust it upwards
			backgroundY = panelBottomEdge - backgroundHeight - 5;
		}
		textY = backgroundY + backgroundHeight / 2; // Adjust textY based on the new backgroundY

		// Draw background and border for the label (optional)
		if (this.params.style.label.background) {
			ctx.save();
			const border = this.params.style.label.background.border;
			const radius = this.params.style.label.background.radius || 0;
			const fillStyle = this.params.style.label.background.color;
			const strokeStyle = border.color ? {
				color: border.color,
				width: border.width,
				lineDash: border.lineDash
			} : null;

			this._drawRoundedRect(ctx, backgroundX, backgroundY, backgroundWidth, backgroundHeight, radius, fillStyle, strokeStyle);

			ctx.restore();
		}

		// Draw the label text
		ctx.fillText(
			labelText,
			textX,
			textY
		);

		// Handles
		this.handle[0].render();
		this.handle[1].render();

		this.panel.core.onToolRender(this);
	}


	// Function to draw a rounded rectangle
	_drawRoundedRect(ctx, x, y, width, height, radius, fillStyle, strokeStyle) {
		// Draw the path of the rounded rectangle
		ctx.beginPath();
		ctx.moveTo(x + radius, y);
		ctx.arcTo(x + width, y, x + width, y + height, radius);
		ctx.arcTo(x + width, y + height, x, y + height, radius);
		ctx.arcTo(x, y + height, x, y, radius);
		ctx.arcTo(x, y, x + width, y, radius);
		ctx.closePath();

		// Apply fill style if provided
		if (fillStyle) {
			ctx.fillStyle = fillStyle;
			ctx.fill();
		}

		// Apply stroke style if provided
		if (strokeStyle) {
			ctx.strokeStyle = strokeStyle.color;
			ctx.lineWidth = strokeStyle.width;
			if (strokeStyle.lineDash) {
				ctx.setLineDash(strokeStyle.lineDash.split(/\s*,\s*/));
			}
			ctx.stroke();
			ctx.setLineDash([]); // Reset line dash
		}
	}


	distance(coord1, coord2) {
		let xs = coord2[0] - coord1[0];
		let ys = coord2[1] - coord1[1];

		xs *= xs;
		ys *= ys;

		return Math.sqrt(xs + ys);
	}
}
